import { useSettings } from "@utilities/context/settings";
import clsx from "clsx";
import { useRef, useState } from "react";
import IconCross from "../../public/image/svg/cross.svg";
import styles from "./C203_PopularTopics.module.scss";
import { determinePageType } from "./topicConfig";

export interface PopularTopicsProps {
  heading: string;
  topics: string[];
  viewAllLabel: string;
}

const MAX_VISIBLE_TOPICS = 7;

const PopularTopics = ({
  heading,
  topics,
  viewAllLabel,
}: PopularTopicsProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const firstHiddenTopicRef = useRef<HTMLAnchorElement | null>(null);
  const { siteSettings } = useSettings();

  const handleToggle = () => {
    setIsExpanded((prev) => {
      const newExpanded = !prev;

      if (newExpanded) {
        window.setTimeout(() => {
          firstHiddenTopicRef.current?.focus();
        }, 0);
      }

      return newExpanded;
    });
  };

  // Get item from siteSettings based on topic type
  const getSlugByType = (topic: string) => {
    if (!siteSettings?.searchResultsPagesCollection?.items) return null;
    const type = determinePageType(topic);
    return siteSettings.searchResultsPagesCollection.items.find(
      (item) => item.type === type
    );
  };

  // Extract topic slug and label from string "type:label"
  const parseTopic = (topic: string) => {
    const parts = topic.split(":");
    const label = parts.length > 1 ? parts[1] : parts[0];
    const slugItem = getSlugByType(label.toLowerCase());
    const slug = slugItem?.url?.slug ?? "";
    return { label, slug };
  };

  return (
    <section className={clsx("component", styles["popular-topics"])}>
      <div className={clsx("wrapper lg:flex items-start gap-16")}>
        <h2 className={styles["popular-topics__heading"]}>{heading}</h2>
        {topics?.length > 0 && (
          <ul
            className={clsx(
              "flex flex-wrap items-start gap-4 xl:gap-10",
              styles["popular-topics__list"],
              { "lg:flex-nowrap": !isExpanded }
            )}
          >
            {topics.map((topic, index) => {
              const { slug, label } = parseTopic(topic);
              const href = `/${slug}?topics=${label}`;
              const isHidden = !isExpanded && index >= MAX_VISIBLE_TOPICS;
              return (
                <li
                  key={label + index}
                  className={clsx(styles["popular-topics__item"], {
                    [styles["popular-topics__item--hidden"]]: isHidden,
                  })}
                  aria-hidden={isHidden ? "true" : undefined}
                >
                  <a
                    tabIndex={isHidden ? -1 : 0}
                    href={href}
                    ref={
                      index === MAX_VISIBLE_TOPICS ? firstHiddenTopicRef : null
                    }
                  >
                    {label}
                  </a>
                </li>
              );
            })}
            {!isExpanded && topics.length > MAX_VISIBLE_TOPICS && (
              <li className={styles["popular-topics__view-all"]}>
                <button
                  className={styles["popular-topics__view-all-button"]}
                  onClick={handleToggle}
                  aria-expanded={isExpanded}
                  aria-label="View all topics"
                >
                  <IconCross />
                  {viewAllLabel}
                </button>
              </li>
            )}
          </ul>
        )}
      </div>
    </section>
  );
};

PopularTopics.displayName = "C203_PopularTopics";

export default PopularTopics;
